import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import api from '../../services/api';
import type { AccountBudgetConfigResponse } from '../../types/accountBudget';
import type {
  AccountTrendFocus,
  AccountTrendGranularity,
  AccountTrendMetric,
  AccountTrendResponse
} from '../../types/accountTrend';
import { metricFromBudgetType } from '../../types/accountTrend';
import {
  focusAnchorFromBucket,
  getTodayAnchor,
  normalizeFocusAnchor,
  shiftFocusAnchor
} from '../../utils/accountDashboardFocus';

const METRIC_STORAGE_PREFIX = 'accountTrendMetric:';

function readStoredMetric(accountId: string): AccountTrendMetric | null {
  try {
    const raw = sessionStorage.getItem(`${METRIC_STORAGE_PREFIX}${accountId}`);
    if (raw === 'hours' || raw === 'internal_cost' || raw === 'billable') {
      return raw;
    }
  } catch {
    // ignore
  }
  return null;
}

function writeStoredMetric(accountId: string, metric: AccountTrendMetric): void {
  try {
    sessionStorage.setItem(`${METRIC_STORAGE_PREFIX}${accountId}`, metric);
  } catch {
    // ignore
  }
}

export interface AccountDashboardContextValue {
  accountId: string;
  accessGranted: boolean | null;
  budgetConfig: AccountBudgetConfigResponse | null;
  metric: AccountTrendMetric;
  granularity: AccountTrendGranularity;
  anchor: string;
  focus: AccountTrendFocus | null;
  focusWindow: { from: string; to: string; label: string } | null;
  /** @deprecated Use focusWindow */
  viewWindow: { from: string; to: string; label: string } | null;
  trendData: AccountTrendResponse | null;
  trendLoading: boolean;
  trendRefreshing: boolean;
  trendError: string | null;
  currencyCode: string;
  accountTimezone: string;
  setMetric: (metric: AccountTrendMetric) => void;
  setGranularity: (granularity: AccountTrendGranularity) => void;
  goPrev: () => void;
  goNext: () => void;
  setFocusFromBucket: (bucket: { key: string; from: string; to: string }) => void;
  refreshBudgetConfig: () => Promise<void>;
}

const AccountDashboardContext = createContext<AccountDashboardContextValue | null>(null);

export function useAccountDashboard(): AccountDashboardContextValue {
  const ctx = useContext(AccountDashboardContext);
  if (!ctx) {
    throw new Error('useAccountDashboard must be used within AccountDashboardProvider');
  }
  return ctx;
}

export function useAccountDashboardOptional(): AccountDashboardContextValue | null {
  return useContext(AccountDashboardContext);
}

interface AccountDashboardProviderProps {
  accountId: string;
  accountCurrency: string;
  children: React.ReactNode;
}

export function AccountDashboardProvider({
  accountId,
  accountCurrency,
  children
}: AccountDashboardProviderProps) {
  const [accessGranted, setAccessGranted] = useState<boolean | null>(null);
  const [budgetConfig, setBudgetConfig] = useState<AccountBudgetConfigResponse | null>(null);
  const [metric, setMetricState] = useState<AccountTrendMetric>('hours');
  const [granularity, setGranularityState] = useState<AccountTrendGranularity>('month');
  const [anchor, setAnchor] = useState(() => normalizeFocusAnchor(getTodayAnchor(), 'month'));
  const [trendData, setTrendData] = useState<AccountTrendResponse | null>(null);
  const [trendLoading, setTrendLoading] = useState(true);
  const [trendRefreshing, setTrendRefreshing] = useState(false);
  const [trendError, setTrendError] = useState<string | null>(null);
  const budgetTypeRef = useRef<string | null>(null);
  const trendRequestRef = useRef(0);
  const trendDataRef = useRef<AccountTrendResponse | null>(null);
  trendDataRef.current = trendData;

  const refreshBudgetConfig = useCallback(async () => {
    try {
      setTrendError(null);
      const res = await api.getAccountBudget(accountId);
      if (res.success) {
        setAccessGranted(true);
        const data = res.data as AccountBudgetConfigResponse;
        setBudgetConfig(data);

        const nextBudget = data.budget;
        const defaultMetric = nextBudget?.isActive
          ? metricFromBudgetType(nextBudget.type)
          : 'hours';
        const stored = readStoredMetric(accountId);
        const budgetTypeChanged = budgetTypeRef.current !== (nextBudget?.type ?? null);
        budgetTypeRef.current = nextBudget?.type ?? null;

        if (stored && !budgetTypeChanged) {
          setMetricState(stored);
        } else {
          setMetricState(defaultMetric);
          writeStoredMetric(accountId, defaultMetric);
        }
      } else {
        setAccessGranted(false);
        setBudgetConfig(null);
      }
    } catch (err: unknown) {
      const status =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;
      if (status === 403) {
        setAccessGranted(false);
        setBudgetConfig(null);
        return;
      }
      setAccessGranted(false);
      setBudgetConfig(null);
    }
  }, [accountId]);

  useEffect(() => {
    setAccessGranted(null);
    setBudgetConfig(null);
    setTrendData(null);
    setAnchor(normalizeFocusAnchor(getTodayAnchor(), 'month'));
    refreshBudgetConfig();
  }, [accountId, refreshBudgetConfig]);

  const setMetric = useCallback(
    (next: AccountTrendMetric) => {
      setMetricState(next);
      writeStoredMetric(accountId, next);
    },
    [accountId]
  );

  const setGranularity = useCallback((next: AccountTrendGranularity) => {
    setGranularityState(next);
    setAnchor(normalizeFocusAnchor(getTodayAnchor(), next));
  }, []);

  const goPrev = useCallback(() => {
    setAnchor((prev) => shiftFocusAnchor(prev, granularity, -1));
  }, [granularity]);

  const goNext = useCallback(() => {
    setAnchor((prev) => shiftFocusAnchor(prev, granularity, 1));
  }, [granularity]);

  const setFocusFromBucket = useCallback(
    (bucket: { key: string; from: string; to: string }) => {
      setAnchor(focusAnchorFromBucket(bucket.from, granularity));
    },
    [granularity]
  );

  const loadTrend = useCallback(async () => {
    if (!accessGranted) {
      setTrendLoading(false);
      setTrendRefreshing(false);
      return;
    }

    const requestId = ++trendRequestRef.current;
    const hasExistingData = trendDataRef.current !== null;

    try {
      if (hasExistingData) {
        setTrendRefreshing(true);
      } else {
        setTrendLoading(true);
      }
      setTrendError(null);

      const res = await api.getAccountTrend(accountId, { metric, granularity, anchor });
      if (requestId !== trendRequestRef.current) {
        return;
      }

      if (res.success) {
        setTrendData(res.data as AccountTrendResponse);
      } else {
        setTrendData(null);
        setTrendError(res.message ?? 'Failed to load account trend');
      }
    } catch (err: unknown) {
      if (requestId !== trendRequestRef.current) {
        return;
      }
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setTrendData(null);
      setTrendError(message ?? 'Failed to load account trend');
    } finally {
      if (requestId === trendRequestRef.current) {
        setTrendLoading(false);
        setTrendRefreshing(false);
      }
    }
  }, [accessGranted, accountId, metric, granularity, anchor]);

  useEffect(() => {
    loadTrend();
  }, [loadTrend]);

  const focus = trendData?.focus ?? null;

  const focusWindow = useMemo(() => {
    if (!focus) return null;
    return {
      from: focus.from,
      to: focus.to,
      label: focus.label
    };
  }, [focus]);

  const currencyCode =
    trendData?.meta.currencyCode ??
    budgetConfig?.budget?.currencyCode ??
    accountCurrency;

  const accountTimezone = trendData?.meta.timezone ?? 'UTC';

  const value = useMemo(
    (): AccountDashboardContextValue => ({
      accountId,
      accessGranted,
      budgetConfig,
      metric,
      granularity,
      anchor,
      focus,
      focusWindow,
      viewWindow: focusWindow,
      trendData,
      trendLoading,
      trendRefreshing,
      trendError,
      currencyCode,
      accountTimezone,
      setMetric,
      setGranularity,
      goPrev,
      goNext,
      setFocusFromBucket,
      refreshBudgetConfig
    }),
    [
      accountId,
      accessGranted,
      budgetConfig,
      metric,
      granularity,
      anchor,
      focus,
      focusWindow,
      trendData,
      trendLoading,
      trendRefreshing,
      trendError,
      currencyCode,
      accountTimezone,
      setMetric,
      setGranularity,
      goPrev,
      goNext,
      setFocusFromBucket,
      refreshBudgetConfig
    ]
  );

  return (
    <AccountDashboardContext.Provider value={value}>{children}</AccountDashboardContext.Provider>
  );
}
